// sessionStorage-backed cache for the slow-changing lists the calendar screens (Daily,
// CalMatches, MatchEvents) all load: calendars, events and channels. Those change rarely and
// only through this admin panel, so a per-tab copy that is dropped on every write (see the
// invalidateSessionCache calls in AdminService) is enough - no TTL, no cross-tab sync.
//
// Every hit is JSON.parse'd afresh, so callers can sort/edit what they get back without
// corrupting the cached copy.

export const SESSION_CACHE_KEYS = {
    calLeagues: 'cache.calLeaguesList',
    events: 'cache.eventsList',
    channels: 'cache.channelsList',
    reportGroups: 'cache.reportGroups'
}

// Per-encoder xaccel stream lists (XaccelService.getXaccelStreamIds with cache=true) - one
// key per encoder, so they're cleared by prefix rather than listed in SESSION_CACHE_KEYS.
export const XACCEL_STREAM_IDS_KEY_PREFIX = 'cache.xaccelStreamIds.'

export function xaccelStreamIdsKey(encoder: string) {
    return XACCEL_STREAM_IDS_KEY_PREFIX + encoder
}

// Requests currently on the wire, by key - two callers asking for the same list at the same
// moment (e.g. two components created together) share one request instead of firing two.
const inFlight = new Map<string, Promise<any>>()

const clone = <T>(data: T): T => JSON.parse(JSON.stringify(data)) as T

export function cachedInSession<T>(key: string, fetcher: () => Promise<T>): Promise<T> {
    try {
        const stored = sessionStorage.getItem(key);
        if (stored) {
            return Promise.resolve(JSON.parse(stored) as T);
        }
    } catch (e) {
        // unreadable/corrupt entry - fall through to the network
    }
    const pending = inFlight.get(key);
    if (pending) {
        return pending.then(clone);
    }
    const request: Promise<T> = fetcher().then((data) => {
        // Skip the write if the key was invalidated while this request was in flight.
        if (inFlight.get(key) === request) {
            inFlight.delete(key);
            try {
                sessionStorage.setItem(key, JSON.stringify(data));
            } catch (e) {
                // storage full or unavailable - just don't cache this one
            }
        }
        return data;
    }, (error) => {
        if (inFlight.get(key) === request) {
            inFlight.delete(key);
        }
        throw error;
    });
    inFlight.set(key, request);
    return request;
}

export function invalidateSessionCache(...keys: string[]) {
    keys.forEach((key) => {
        inFlight.delete(key);
        try {
            sessionStorage.removeItem(key);
        } catch (e) {
            // nothing to clear
        }
    });
}

export function clearAllSessionCaches() {
    invalidateSessionCache(...Object.values(SESSION_CACHE_KEYS));
    try {
        const prefixed: string[] = [];
        for (let i = 0; i < sessionStorage.length; i++) {
            const key = sessionStorage.key(i);
            if (key && key.startsWith(XACCEL_STREAM_IDS_KEY_PREFIX)) prefixed.push(key);
        }
        invalidateSessionCache(...prefixed);
    } catch (e) {
        // storage unavailable - nothing to clear
    }
}
